import Layout from "@/components/site/Layout";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { LocaleLink as Link } from "@/i18n/LocaleLink";
import { tx } from "@/i18n/LocaleProvider";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight, BookOpen, Sparkles, Clock, Check, Plus } from "lucide-react";
import { subjects } from "@/data/content";
import { useRef, useState, type ComponentType } from "react";

type Feature = {
  n: string;
  icon: ComponentType<{ className?: string }>;
  title: string;
  text: string;
  details: string[];
};

function useFeatures(): Feature[] {
  // i18n on subscribe via useTranslation triggers re-render on language change.
  useTranslation();
  return [
    {
      n: "01",
      icon: Clock,
      title: tx("Recapitulare condensată", "Condensed review"),
      text: tx(
        "Esențialul din fiecare materie, structurat pentru ultimele săptămâni. Fără timp pierdut.",
        "The essentials of every subject, structured for the final weeks of prep. No wasted time."
      ),
      details: [
        tx(
          "Lecții scurte de 10–15 minute, gândite pentru sesiuni reale de studiu între ore.",
          "Short 10–15 minute lessons, built for real study sessions between classes."
        ),
        tx(
          "Fișe sintetice cu definiții, autori, opere și concepte cheie — ideal pentru recapitulare rapidă.",
          "Synthesis sheets with definitions, authors, works and key concepts — perfect for fast review."
        ),
        tx(
          "Conținut aliniat cu programa Ministerului Educației pentru BAC 2026.",
          "Content aligned with the national high-school exit exam curriculum for 2026."
        ),
      ],
    },
    {
      n: "02",
      icon: BookOpen,
      title: tx("Eseuri model + simulări", "Model essays + mock exams"),
      text: tx(
        "Subiecte rezolvate pas cu pas, modele de eseu și teste tip BAC ca să intri pregătit în sală.",
        "Past papers solved step by step, model essays and exam-style tests so you walk in prepared."
      ),
      details: [
        tx(
          "Modele de eseu pentru toate operele canonice cerute la BAC.",
          "Model essays for every canonical work on the curriculum."
        ),
        tx(
          "Subiecte din variantele oficiale, rezolvate cu explicații pe puncte.",
          "Official past-paper questions, solved with point-by-point explanations."
        ),
        tx(
          "Quiz-uri interactive după fiecare lecție pentru a fixa informația.",
          "Interactive quizzes after every lesson to lock in what you learned."
        ),
      ],
    },
    {
      n: "03",
      icon: Sparkles,
      title: tx("Garanția 9+", "9+ guarantee"),
      text: tx(
        "Dacă urmezi planul și nu iei minim 9 la BAC, primești toți banii înapoi. Risc zero.",
        "Follow the plan and if you don't hit at least a 9 on the exam, you get every cent back. Zero risk."
      ),
      details: [
        tx(
          "Parcurgi planul de studiu recomandat pentru materiile alese.",
          "You follow the recommended study plan for your chosen subjects."
        ),
        tx(
          "Susții BAC-ul în sesiunea iunie–iulie 2026.",
          "You sit the exam in the June–July 2026 session."
        ),
        tx(
          "Dacă nu obții minim nota 9, ne trimiți dovada și îți returnăm integral valoarea abonamentului.",
          "If you don't reach a 9, send proof and we refund your subscription in full."
        ),
      ],
    },
  ];
}

function FeatureItem({ f }: { f: Feature }) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), 450);
  };
  const handleLeave = () => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(false);
  };

  return (
    <li
      className="group grid grid-cols-12 items-start gap-4 py-8 transition-smooth hover:pl-2"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
    >
      <div className="col-span-2 font-mono text-sm text-muted-foreground md:col-span-1">{f.n}</div>
      <div className="col-span-10 md:col-span-9">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-2xl font-semibold md:text-3xl">{f.title}</h3>
          <Plus
            className={`mt-1 hidden h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300 md:block ${
              open ? "rotate-45 text-accent" : ""
            }`}
            aria-hidden
          />
        </div>
        <p className="mt-2 max-w-xl text-muted-foreground">{f.text}</p>
        <div
          className={`grid transition-all duration-500 ease-out ${
            open ? "mt-5 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <ul className="max-w-xl space-y-2 border-l-2 border-accent/40 pl-4 text-sm text-muted-foreground">
              {f.details.map((d, i) => (
                <li key={i} className="flex gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="col-span-12 mt-2 md:col-span-2 md:mt-0 md:text-right">
        <span className="inline-grid h-10 w-10 place-items-center rounded-full bg-muted text-foreground transition-spring group-hover:bg-accent group-hover:text-accent-foreground md:ml-auto">
          <f.icon className="h-4 w-4" />
        </span>
      </div>
    </li>
  );
}

const heroImg = "/hero-learning.jpg";
const heroImgWebp = "/hero-learning.webp";
const heroImgMobileWebp = "/hero-learning-mobile.webp";

const Home = () => {
  useTranslation(); // subscribe so tx() reacts to language switch
  const features = useFeatures();
  return (
    <Layout>
      <Seo
        title={tx(
          "Self Educate — Pregătire BAC 2026 pentru elevii români",
          "Self Educate — Self-driven exam prep for high-school students"
        )}
        description={tx(
          "Lecții clare, fișe sintetice, eseuri model și arhivă BAC. Platforma de auto-educare pentru elevii care se pregătesc serios de Bacalaureat.",
          "Clear lessons, synthesis sheets, model essays and past papers. The self-study platform for high-school students preparing seriously for their exit exam."
        )}
        path="/"
      />
      <section className="relative overflow-hidden bg-gradient-soft grain">
        <div className="container relative pt-16 pb-24 md:pt-24 md:pb-32">
          <div className="mb-10 flex flex-wrap items-center justify-between gap-4 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground">
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-accent pulse-glow" /> {tx("Sprintul final · BAC 2026", "Final sprint · Exams 2026")}</span>
            <span className="hidden md:block">{tx("Ultimii 100 de metri → Examen → Reușită", "The last 100 metres → Exam → Win")}</span>
            <span>{tx("Garanție 9+", "9+ guarantee")}</span>
          </div>

          <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7 animate-fade-up">
              <h1 className="font-display text-[clamp(2.75rem,8vw,7rem)] font-bold leading-[0.92] tracking-tight text-balance">
                {tx("Ultimii ", "The last ")}
                <span className="font-serif-italic font-normal text-accent">{tx("100 de metri", "100 metres")}</span>
                <br />
                {tx("până la ", "to the ")}<span className="ink-mark">{tx("BAC", "exam")}</span>.
              </h1>
              <p className="mt-8 max-w-xl text-lg text-foreground text-pretty md:text-xl">
                {tx(
                  "Self Educate este partenerul tău pentru sprintul final: recapitulări concentrate, eseuri model, simulări și exerciții — exact ce-ți trebuie ca să intri în sală fără griji. ",
                  "Self Educate is your partner for the final sprint: focused review, model essays, mock exams and drills — exactly what you need to walk into the room without nerves. "
                )}
                <strong className="text-foreground">
                  {tx("Dacă nu iei minim 9, îți returnăm banii.", "If you don't score at least a 9, we refund you in full.")}
                </strong>
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Button asChild variant="warm" size="xl">
                  <Link to="/pricing">{tx("Vreau să trec BAC-ul", "I'm in — let's go")} <ArrowRight /></Link>
                </Button>
                <Button asChild variant="outline" size="xl">
                  <Link to="/subjects">{tx("Vezi materiile", "Browse subjects")} <ArrowUpRight /></Link>
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground">
                <span className="flex items-center gap-2"><Check className="h-4 w-4 text-accent" /> {tx("Garanție: minim 9 sau banii înapoi", "Guarantee: minimum 9 or your money back")}</span>
                <span className="flex items-center gap-2"><Check className="h-4 w-4 text-accent" /> {tx("Acces imediat", "Instant access")}</span>
                <span className="flex items-center gap-2"><Check className="h-4 w-4 text-accent" /> {tx("Gratuit în beta deschisă · fără card", "Free in open beta · no card required")}</span>
              </div>
            </div>

            <div className="relative lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-6 -rotate-3 rounded-[2.5rem] bg-accent/10 blur-2xl" />
                <div className="relative overflow-hidden rounded-[2rem] shadow-ink animate-float">
                  <picture>
                    <source
                      type="image/webp"
                      srcSet={`${heroImgMobileWebp} 640w, ${heroImgWebp} 780w`}
                      sizes="(min-width: 768px) 780px, 100vw"
                    />
                    <img
                      src={heroImg}
                      alt={tx("Trei studenți zâmbind în timp ce citesc cărți colorate", "Three students smiling while reading colourful books")}
                      width={1040}
                      height={693}
                      fetchPriority="high"
                      decoding="async"
                      className="h-[420px] w-full object-cover md:h-[520px]"
                    />
                  </picture>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/80 to-transparent p-6">
                    <p className="font-display text-xl font-medium text-ink-foreground">
                      {tx("„Mai puțin scroll, mai multă curiozitate.”", "“Less scrolling, more curiosity.”")}
                    </p>
                  </div>
                </div>

                <div className="absolute -left-6 top-10 hidden rotate-[-6deg] rounded-2xl border border-border bg-card px-4 py-3 shadow-card md:block">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{tx("Lecția 01", "Lesson 01")}</div>
                  <div className="font-display text-base font-semibold">{tx("Cum citim un poem", "How to read a poem")}</div>
                </div>
                <div className="absolute -right-4 bottom-16 hidden rotate-[6deg] rounded-2xl bg-ink px-4 py-3 text-ink-foreground shadow-ink md:block">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-ink-foreground/60">{tx("Progres", "Progress")}</div>
                  <div className="font-display text-base font-semibold">{tx("12 / 120 lecții", "12 / 120 lessons")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-background">
        <div className="container grid grid-cols-2 divide-x divide-border md:grid-cols-4">
          {[
            { v: "9+", l: tx("Garantat sau banii înapoi", "Guaranteed or your money back") },
            { v: "100m", l: tx("Sprintul final spre BAC", "Final sprint to exam day") },
            { v: "−74%", l: tx("Ofertă de lansare", "Launch offer") },
            { v: "13", l: tx("Materii pentru BAC", "Subjects on the curriculum") },
          ].map((s) => (
            <div
              key={s.l}
              className="px-6 py-8 [&:nth-child(odd)]:!border-l-0 md:[&:nth-child(odd)]:!border-l md:[&:nth-child(4n+1)]:!border-l-0 [&:nth-child(n+3)]:border-t [&:nth-child(n+3)]:border-border md:[&:nth-child(n+3)]:!border-t-0"
            >
              <div className="font-display text-4xl font-bold tracking-tight md:text-5xl">{s.v}</div>
              <div className="mt-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="container py-24 md:py-32">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{tx("§ 01 — De ce noi", "§ 01 — Why us")}</span>
            <h2 className="mt-4 font-display text-5xl font-bold leading-[0.95] tracking-tight md:text-6xl">
              {tx("Sprijinul real pentru ", "Real support for the ")}
              <span className="font-serif-italic font-normal text-accent">{tx("sprintul final", "final sprint")}</span>.
            </h2>
            <p className="mt-6 text-lg text-muted-foreground text-pretty">
              {tx(
                "Mai sunt câteva săptămâni. Nu ai timp de cursuri vagi. Ai nevoie de structură, exerciții țintite și încredere că treci.",
                "A few weeks left. No time for vague lectures. You need structure, focused drills and confidence that you'll pass."
              )}
            </p>
          </div>
          <div className="lg:col-span-8">
            <ul className="divide-y divide-border border-t border-border">
              {features.map((f) => (
                <FeatureItem key={f.n} f={f} />
              ))}
            </ul>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-muted-foreground/70">
              {tx("Treci cu mouse-ul peste un punct pentru detalii", "Hover over a point for details")}
            </p>
          </div>
        </div>
      </section>

      <section className="bg-ink py-24 text-ink-foreground md:py-32">
        <div className="container">
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6 border-b border-background/15 pb-8">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-foreground/50">{tx("§ 02 — Catalog", "§ 02 — Catalogue")}</span>
              <h2 className="mt-3 font-display text-5xl font-bold leading-[0.95] tracking-tight md:text-6xl">
                {tx("Materiile ", "Our ")}<span className="font-serif-italic font-normal text-accent">{tx("noastre", "subjects")}</span>
              </h2>
            </div>
            <Link to="/subjects" className="link-underline font-mono text-xs uppercase tracking-widest">
              {tx("Toate materiile →", "All subjects →")}
            </Link>
          </div>

          <div className="grid gap-px bg-background/15 sm:grid-cols-2 lg:grid-cols-3">
            {subjects.slice(0, 6).map((s, i) => (
              <Link
                key={s.slug}
                to={s.available ? `/subjects/${s.slug}` : "/subjects"}
                className={`group relative flex min-h-[260px] flex-col justify-between bg-ink p-8 transition-smooth hover:bg-background hover:text-foreground ${
                  !s.available ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-xs text-ink-foreground/75 group-hover:text-muted-foreground">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-3xl">{s.emoji}</span>
                </div>
                <div>
                  <h3 className="font-display text-3xl font-semibold leading-tight">{s.name}</h3>
                  <p className="mt-2 text-sm text-ink-foreground/80 group-hover:text-muted-foreground">{s.tagline}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-ink-foreground/75 group-hover:text-muted-foreground">
                      {s.available ? tx("Disponibil", "Available") : tx("În curând", "Coming soon")}
                    </span>
                    {s.available && (
                      <ArrowUpRight className="h-5 w-5 transition-spring group-hover:rotate-45" />
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>


      <section className="container pb-12">
        <div className="grid gap-px overflow-hidden rounded-[2rem] border border-border bg-border md:grid-cols-2">
          <div className="flex flex-col justify-between gap-6 bg-background p-8 md:p-10">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{tx("Pentru alumni", "For alumni")}</span>
              <h3 className="mt-3 font-display text-3xl font-semibold leading-tight md:text-4xl">
                {tx("Ai dat deja BAC-ul? ", "Already graduated? ")}
                <span className="font-serif-italic font-normal text-accent">{tx("Ajută generația următoare.", "Help the next generation.")}</span>
              </h3>
              <p className="mt-3 text-muted-foreground">
                {tx(
                  "Creează un cont de Alumni verificat și primești o insignă de încredere în comunitate. Răspunzi la întrebări cu experiența ta reală.",
                  "Create a verified Alumni account and get a trust badge in the community. Share answers backed by your real experience."
                )}
              </p>
            </div>
            <Button asChild variant="warm" size="lg" className="self-start">
              <Link to="/auth?role=alumni">{tx("Cont de Alumni", "Alumni account")} <ArrowRight /></Link>
            </Button>
          </div>
          <div className="flex flex-col justify-between gap-6 bg-background p-8 md:p-10">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{tx("Pentru profesori", "For teachers")}</span>
              <h3 className="mt-3 font-display text-3xl font-semibold leading-tight md:text-4xl">
                {tx("Ești profesor? ", "Are you a teacher? ")}
                <span className="font-serif-italic font-normal text-accent">{tx("Ghidează-ți elevii aici.", "Guide your students here.")}</span>
              </h3>
              <p className="mt-3 text-muted-foreground">
                {tx(
                  "Aplică pentru un cont de Profesor verificat și obții o insignă vizibilă în comunitate, ca să-ți ajuți elevii cu sfaturi de încredere.",
                  "Apply for a verified Teacher account and get a visible badge in the community, so you can help students with trusted advice."
                )}
              </p>
            </div>
            <Button asChild variant="outline" size="lg" className="self-start">
              <Link to="/auth?role=teacher">{tx("Cont de Profesor", "Teacher account")} <ArrowUpRight /></Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container pb-24">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-accent p-12 text-accent-foreground shadow-warm md:p-20">
          <div
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
          />
          <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-highlight opacity-40 blur-3xl" />
          <div className="relative grid gap-8 md:grid-cols-12 md:items-end">
            <div className="md:col-span-8">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-accent-foreground/70">{tx("§ 03 — Garanția noastră", "§ 03 — Our guarantee")}</span>
              <h2 className="mt-3 font-display text-5xl font-bold leading-[0.95] tracking-tight md:text-7xl">
                {tx("Iei minim 9 la BAC.", "Score at least a 9 on the exam.")}<br />
                {tx("Sau îți ", "Or get a ")}<span className="font-serif-italic font-normal">{tx("returnăm banii", "full refund")}</span>.
              </h2>
              <p className="mt-6 max-w-xl text-accent-foreground/85">{tx(
                "Suntem atât de siguri pe materialele noastre încât îți garantăm rezultatul. Tu pui efortul, noi punem structura.",
                "We're so confident in our materials that we guarantee the result. You bring the effort; we bring the structure."
              )}</p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Button asChild size="xl" variant="ink">
                <Link to="/pricing">{tx("Începe acum", "Get started")} <ArrowRight /></Link>
              </Button>
              <p className="mt-4 text-sm text-accent-foreground/80">{tx(
                "Gratuit în beta · garanție 9+ · acces imediat",
                "Free in beta · 9+ guarantee · instant access"
              )}</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
